'use client';

import { useState } from 'react';
import { Mail, Phone, MapPin, MessageSquare, Send, CheckCircle } from 'lucide-react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submission
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] px-4 py-24">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="mb-4 font-serif text-5xl font-bold text-white">
            Get In{' '}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#B76E79] bg-clip-text text-transparent">
              Touch
            </span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-400">
            Have questions about VIP packages or need assistance? Our team is here to help 24/7.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Contact Information */}
          <div className="space-y-6 lg:col-span-1">
            {/* Email */}
            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-6 backdrop-blur-md">
              <div className="mb-4 inline-flex rounded-full bg-[#D4AF37]/20 p-3">
                <Mail className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="mb-2 font-serif text-xl font-bold text-white">Email Us</h3>
              <p className="mb-2 text-sm text-gray-400">
                Send us an email and we'll respond within 24 hours
              </p>
              <a
                href="mailto:support@tickettoluxury.com"
                className="text-[#D4AF37] hover:underline"
              >
                support@tickettoluxury.com
              </a>
            </div>

            {/* Phone */}
            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-6 backdrop-blur-md">
              <div className="mb-4 inline-flex rounded-full bg-[#D4AF37]/20 p-3">
                <Phone className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="mb-2 font-serif text-xl font-bold text-white">Call or WhatsApp</h3>
              <p className="mb-2 text-sm text-gray-400">Available 24/7 for urgent inquiries</p>
              <a href="tel:+8801234567890" className="text-[#D4AF37] hover:underline">
                +880 1234-567890
              </a>
            </div>

            {/* Locations */}
            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-6 backdrop-blur-md">
              <div className="mb-4 inline-flex rounded-full bg-[#D4AF37]/20 p-3">
                <MapPin className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="mb-2 font-serif text-xl font-bold text-white">Our Offices</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <div>
                  <p className="font-semibold text-white">Bangladesh</p>
                  <p>Dhaka, Gulshan-2</p>
                </div>
                <div>
                  <p className="font-semibold text-white">India</p>
                  <p>Mumbai, Bandra West</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-6 backdrop-blur-md">
              <div className="mb-4 inline-flex rounded-full bg-[#D4AF37]/20 p-3">
                <MessageSquare className="h-6 w-6 text-[#D4AF37]" />
              </div>
              <h3 className="mb-2 font-serif text-xl font-bold text-white">Follow Us</h3>
              <div className="flex gap-3">
                <a
                  href="#"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                  <span className="sr-only">Facebook</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                  <span className="sr-only">Instagram</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                  <span className="sr-only">Twitter</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/80 to-black/60 p-8 backdrop-blur-md">
              <h2 className="mb-6 font-serif text-2xl font-bold text-white">Send Us a Message</h2>

              {submitted ? (
                <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-8 text-center">
                  <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-400" />
                  <h3 className="mb-2 font-serif text-2xl font-bold text-white">
                    Message Sent!
                  </h3>
                  <p className="text-gray-300">
                    Thank you for contacting us. We'll respond within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-300">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-300">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                      placeholder="+880 1234 567890"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Subject *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 text-white backdrop-blur-md transition-all duration-300 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                    >
                      <option value="">Select a subject</option>
                      <option value="booking">Booking Inquiry</option>
                      <option value="payment">Payment Issue</option>
                      <option value="package">Package Information</option>
                      <option value="event">Event Details</option>
                      <option value="corporate">Corporate/Group Booking</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                      placeholder="Tell us how we can help..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#B76E79] px-8 py-4 font-bold text-black transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
                  >
                    <Send className="h-5 w-5" />
                    Send Message
                  </button>

                  <p className="text-center text-sm text-gray-400">
                    We typically respond within 24 hours during business days
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
